/**
 * Circle-against-world collision for anything that walks on the floor plane.
 *
 * There are two kinds of collision geometry. The occupancy grid is the
 * building: a solid cell is a full-height axis-aligned box filling the cell, a
 * pillar cell is a small box in the cell's centre. On top of that sit the solid
 * props — a half-wall, a piano, a phone booth standing in the open — which are
 * rotated boxes off the grid entirely, held in `GeneratedLevel.propIndex`.
 *
 * Testing the axes separately is cheap but snags on the seam between two
 * diagonally adjacent walls and lets a fast mover skip past thin geometry on a
 * long frame, so instead this:
 *
 *  - substeps the motion so no single sample advances more than half a radius,
 *  - resolves against the *nearest point* on every overlapping box, deepest
 *    first, iterating until nothing overlaps (an interior corner needs two
 *    passes, a corner plus a pillar three),
 *  - alternates grid and prop resolution, because being pushed off a prop can
 *    push the mover into a wall and vice versa,
 *  - cancels only the component of velocity that runs into the surface, which
 *    is what produces a clean slide,
 *  - and falls back to the frame's starting position if resolution ever fails,
 *    so the caller can never end up embedded in geometry.
 *
 * Resolving the deepest overlap first is what kills the diagonal-corner wedge:
 * the face of the wall the mover is actually against always penetrates deeper
 * than the shared corner of its neighbour, so the corner never gets a say.
 */

import { TUNING } from '../core/config';
import { EMPTY_PROP_INDEX, type PropIndex } from '../core/props';
import { Cell, type GeneratedLevel, type Grid, type Vec2 } from '../core/types';

/**
 * Half-width of a pillar's blocking box, as a fraction of the cell size. A
 * pillar occupies the middle of an otherwise walkable cell; the rest of the
 * cell must stay comfortably passable. Re-exported from TUNING so the pillar
 * mesh and this box can never drift apart.
 */
export const PILLAR_HALF_FRACTION = TUNING.player.pillarHalfFraction;

/** Gap left between the circle and a surface after a push-out, in metres. */
const SKIN = 1e-3;

/** Resolution passes per substep. Three is the worst realistic case. */
const MAX_PASSES = 6;

/**
 * Alternating grid/prop rounds per resolution. A prop wedged against a wall
 * needs the second round to settle; past that the geometry has no solution and
 * `move` reverts instead. The last round is always the grid's, because a wall
 * can never be escaped whereas one frame slightly inside a prop is recoverable.
 */
const MAX_ROUNDS = 3;

/** Fraction of the radius one substep may advance. Below 1 there is no tunnelling. */
const SUBSTEP_FRACTION = 0.5;

/** Hard cap on substeps so a pathological impulse cannot stall the frame. */
const MAX_SUBSTEPS = 48;

/** Scratch capacity in boxes. A circle smaller than a cell touches at most four. */
const MAX_BOXES = 64;

export interface CollisionWorld {
  /**
   * Swap the level whose grid and solid props are collided against. Null
   * disables collision.
   */
  setLevel(level: GeneratedLevel | null): void;

  /**
   * Slide a circle from `pos` by (dx, dz). Mutates `pos`, and if `vel` is given
   * cancels the part of it that runs into a surface. Returns true when the
   * circle touched something.
   */
  move(pos: Vec2, vel: Vec2 | null, dx: number, dz: number, radius: number): boolean;

  /** True when a circle at this point overlaps any blocking box. */
  overlaps(x: number, z: number, radius: number): boolean;

  /** Push a circle out of anything it is inside. Returns true when it had to move. */
  resolve(pos: Vec2, radius: number, vel?: Vec2 | null): boolean;

  /**
   * Nudge `pos` to the nearest spot where a circle of `radius` fits, clear of
   * both walls and props, searching a fixed spiral so the result is the same on
   * every run. False when the search gave up, which means the caller was placed
   * somewhere hopeless.
   */
  findFree(pos: Vec2, radius: number): boolean;
}

class GridCollision implements CollisionWorld {
  private grid: Grid | null = null;
  private props: PropIndex = EMPTY_PROP_INDEX;
  private cellSize = 3;
  private pillarHalf = 0.6;

  /** Flat scratch list of boxes: minX, minZ, maxX, maxZ per entry. */
  private readonly boxes = new Float64Array(MAX_BOXES * 4);
  private boxCount = 0;

  /** Deepest overlap found by `probe`, valid only while it returns true. */
  private hitDepth = 0;
  private hitNX = 0;
  private hitNZ = 0;

  setLevel(level: GeneratedLevel | null): void {
    this.grid = level ? level.grid : null;
    // The index is built with the level and shared with the entities, so a prop
    // that stops the player stops a hound in the same place.
    this.props = level ? level.propIndex : EMPTY_PROP_INDEX;
    this.cellSize = level ? level.grid.cellSize : 3;
    this.pillarHalf = this.cellSize * PILLAR_HALF_FRACTION;
  }

  move(pos: Vec2, vel: Vec2 | null, dx: number, dz: number, radius: number): boolean {
    if (!this.grid) {
      pos.x += dx;
      pos.z += dz;
      return false;
    }

    const startX = pos.x;
    const startZ = pos.z;
    // Remember whether we began embedded: if so, ending embedded is not a
    // regression and reverting would trap the caller forever.
    const startedInside = this.overlaps(startX, startZ, radius);

    let touched = false;
    const dist = Math.hypot(dx, dz);
    if (dist < 1e-9) {
      // Standing still still resolves: a prop can be built on top of a mover
      // that never moves again, and the check below is what gets them out.
      touched = this.resolve(pos, radius, vel);
    } else {
      const step = Math.max(radius * SUBSTEP_FRACTION, 1e-3);
      let steps = Math.ceil(dist / step);
      let mx = dx;
      let mz = dz;
      if (steps > MAX_SUBSTEPS) {
        // Refuse the excess rather than taking bigger steps; nothing legitimate
        // asks to travel this far in one frame.
        const scale = (MAX_SUBSTEPS * step) / dist;
        mx *= scale;
        mz *= scale;
        steps = MAX_SUBSTEPS;
      }

      const sx = mx / steps;
      const sz = mz / steps;
      for (let i = 0; i < steps; i++) {
        pos.x += sx;
        pos.z += sz;
        if (this.resolve(pos, radius, vel)) touched = true;
      }
    }

    if (this.overlaps(pos.x, pos.z, radius)) {
      if (!startedInside) {
        // The invariant matters more than the motion: the start of the frame
        // was known clear, so retreat there rather than finish inside a wall.
        pos.x = startX;
        pos.z = startZ;
        if (vel) {
          vel.x = 0;
          vel.z = 0;
        }
        return true;
      }
      // Started embedded and could not push free. That is a prop lying across
      // a wall, where every push-out is straight into the other surface and the
      // two shove the mover back and forth forever. Being moved a metre is a
      // moment's confusion; being unable to move at all is the end of the run,
      // so take the search and get out.
      this.findFree(pos, radius);
      return true;
    }
    return touched;
  }

  overlaps(x: number, z: number, radius: number): boolean {
    if (!this.grid) return false;
    this.gather(x, z, radius);
    if (this.probe(x, z, radius)) return true;
    // Props shorter than `stepOverHeight` are stepped over, which the index
    // already accounts for — `blocked` applies the same cut-off.
    return this.props.blocked(x, z, radius);
  }

  resolve(pos: Vec2, radius: number, vel: Vec2 | null = null): boolean {
    if (!this.grid) return false;
    let moved = false;
    for (let round = 0; round < MAX_ROUNDS; round++) {
      if (this.resolveGrid(pos, radius, vel)) moved = true;
      // The grid gets the last word, so the round that ends the loop is always
      // a wall round.
      if (round === MAX_ROUNDS - 1) break;
      if (!this.resolveProps(pos, radius, vel)) break;
      moved = true;
    }
    return moved;
  }

  findFree(pos: Vec2, radius: number): boolean {
    if (!this.overlaps(pos.x, pos.z, radius)) return true;
    this.resolve(pos, radius, null);
    if (!this.overlaps(pos.x, pos.z, radius)) return true;

    const step = this.cellSize * 0.5;
    for (let ring = 1; ring <= 6; ring++) {
      for (let i = 0; i < 12; i++) {
        const a = (i / 12) * Math.PI * 2;
        const x = pos.x + Math.cos(a) * step * ring;
        const z = pos.z + Math.sin(a) * step * ring;
        if (!this.overlaps(x, z, radius)) {
          pos.x = x;
          pos.z = z;
          return true;
        }
      }
    }
    return false;
  }

  /** The original grid resolution: deepest box first, until nothing overlaps. */
  private resolveGrid(pos: Vec2, radius: number, vel: Vec2 | null): boolean {
    let moved = false;
    for (let pass = 0; pass < MAX_PASSES; pass++) {
      this.gather(pos.x, pos.z, radius);
      if (!this.probe(pos.x, pos.z, radius)) break;
      const nx = this.hitNX;
      const nz = this.hitNZ;
      pos.x += nx * (this.hitDepth + SKIN);
      pos.z += nz * (this.hitDepth + SKIN);
      this.cancelInto(vel, nx, nz);
      moved = true;
    }
    return moved;
  }

  /**
   * Push out of every solid prop the circle overlaps. The index does the box
   * work and leaves its own skin, so all this adds is the velocity cancel,
   * which it derives from the direction the circle was actually shoved.
   */
  private resolveProps(pos: Vec2, radius: number, vel: Vec2 | null): boolean {
    if (this.props.count === 0) return false;
    const fromX = pos.x;
    const fromZ = pos.z;
    if (!this.props.resolveCircle(pos, radius, TUNING.player.stepOverHeight)) return false;

    const dx = pos.x - fromX;
    const dz = pos.z - fromZ;
    const l = Math.hypot(dx, dz);
    if (l < 1e-9) return true;
    const nx = dx / l;
    const nz = dz / l;
    this.cancelInto(vel, nx, nz);
    return true;
  }

  /** Remove the part of `vel` heading into a surface. What makes a slide slide. */
  private cancelInto(vel: Vec2 | null, nx: number, nz: number): void {
    if (!vel) return;
    const into = vel.x * nx + vel.z * nz;
    if (into < 0) {
      vel.x -= nx * into;
      vel.z -= nz * into;
    }
  }

  /** Collect every blocking box overlapping the circle's bounding square. */
  private gather(x: number, z: number, radius: number): void {
    this.boxCount = 0;
    const grid = this.grid;
    if (!grid) return;
    const cs = this.cellSize;
    const minCX = Math.floor((x - radius) / cs);
    const maxCX = Math.floor((x + radius) / cs);
    const minCZ = Math.floor((z - radius) / cs);
    const maxCZ = Math.floor((z + radius) / cs);

    for (let cy = minCZ; cy <= maxCZ; cy++) {
      for (let cx = minCX; cx <= maxCX; cx++) {
        if (this.boxCount >= MAX_BOXES) return;
        const o = this.boxCount * 4;
        // Out of bounds reads as Void, so the level border is solid for free.
        if (grid.isSolid(cx, cy)) {
          this.boxes[o] = cx * cs;
          this.boxes[o + 1] = cy * cs;
          this.boxes[o + 2] = (cx + 1) * cs;
          this.boxes[o + 3] = (cy + 1) * cs;
          this.boxCount++;
        } else if (grid.at(cx, cy) === Cell.Pillar) {
          const midX = (cx + 0.5) * cs;
          const midZ = (cy + 0.5) * cs;
          const h = this.pillarHalf;
          this.boxes[o] = midX - h;
          this.boxes[o + 1] = midZ - h;
          this.boxes[o + 2] = midX + h;
          this.boxes[o + 3] = midZ + h;
          this.boxCount++;
        }
      }
    }
  }

  /**
   * Find the deepest overlap in the gathered set. Writes hitDepth/hitNX/hitNZ
   * and returns false when the circle is clear.
   */
  private probe(x: number, z: number, radius: number): boolean {
    let bestDepth = 0;
    let bestNX = 0;
    let bestNZ = 0;

    for (let i = 0; i < this.boxCount; i++) {
      const o = i * 4;
      const minX = this.boxes[o]!;
      const minZ = this.boxes[o + 1]!;
      const maxX = this.boxes[o + 2]!;
      const maxZ = this.boxes[o + 3]!;

      const qx = x < minX ? minX : x > maxX ? maxX : x;
      const qz = z < minZ ? minZ : z > maxZ ? maxZ : z;
      const dx = x - qx;
      const dz = z - qz;
      const d2 = dx * dx + dz * dz;

      let depth: number;
      let nx: number;
      let nz: number;
      if (d2 > 1e-12) {
        const d = Math.sqrt(d2);
        depth = radius - d;
        if (depth <= 0) continue;
        nx = dx / d;
        nz = dz / d;
      } else {
        // Centre is inside the box. Leave through the nearest face, which is
        // the only choice that cannot shove the mover through the wall.
        const toMinX = x - minX;
        const toMaxX = maxX - x;
        const toMinZ = z - minZ;
        const toMaxZ = maxZ - z;
        const m = Math.min(toMinX, toMaxX, toMinZ, toMaxZ);
        if (m === toMinX) {
          nx = -1;
          nz = 0;
        } else if (m === toMaxX) {
          nx = 1;
          nz = 0;
        } else if (m === toMinZ) {
          nx = 0;
          nz = -1;
        } else {
          nx = 0;
          nz = 1;
        }
        depth = m + radius;
      }

      if (depth > bestDepth) {
        bestDepth = depth;
        bestNX = nx;
        bestNZ = nz;
      }
    }

    if (bestDepth <= 0) return false;
    this.hitDepth = bestDepth;
    this.hitNX = bestNX;
    this.hitNZ = bestNZ;
    return true;
  }
}

/** Create a collision world. Give it a level with `setLevel` before use. */
export function makeCollisionWorld(): CollisionWorld {
  return new GridCollision();
}
